import React, { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Button,
  Card,
  Col,
  DatePicker,
  Input,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Typography
} from 'antd'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import {
  getRequest,
  vehicleHistoryOrdersUrl,
  vehicleHistoryProductsUrl
} from '../../config'
import { AttentionTag, ChannelTag, StatusTag } from './tags'
import {
  CHANNEL_LABELS,
  MARKET_LABELS,
  MARKET_ORDER,
  STATUS_LABELS,
  buildOrdersQuery,
  formatDateTime,
  money
} from './utils'

const { RangePicker } = DatePicker

/* eslint-disable react/display-name */
const orderColumns = () => [
  {
    title: 'Number',
    key: 'identifier',
    render: (_, row) => (
      <div>
        <Link to={`/vehicle-history/orders/${row.id}`}>
          <Typography.Text strong>{row.identifier}</Typography.Text>
        </Link>
        <div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.identifierKind === 'jp_chassis' ? 'Chassis number' : 'VIN'}
          </Typography.Text>
        </div>
      </div>
    )
  },
  {
    title: 'Package',
    key: 'product',
    render: (_, row) => (
      <div>
        <div>{row.productName || row.productKey || '—'}</div>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {MARKET_LABELS[row.market] || row.market}
        </Typography.Text>
      </div>
    )
  },
  {
    title: 'Came from',
    key: 'channel',
    render: (_, row) => <ChannelTag channel={row.channel} />
  },
  {
    title: 'Status',
    key: 'status',
    render: (_, row) => <StatusTag status={row.status} />
  },
  {
    title: 'Needs attention',
    key: 'attention',
    render: (_, row) =>
      row.attention ? (
        <AttentionTag
          kind={row.attention}
          priority={row.attentionRank === 0 ? 'urgent' : row.attentionRank === 1 ? 'high' : 'low'}
          since={row.attentionSince}
        />
      ) : (
        <Typography.Text type="secondary">—</Typography.Text>
      )
  },
  {
    title: 'Paid',
    key: 'paid',
    render: (_, row) =>
      row.paid ? (
        <div>
          <div>{money(row.priceAmount, row.priceCurrency)}</div>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {row.contactPhone || '—'}
          </Typography.Text>
        </div>
      ) : (
        <Typography.Text type="secondary">Not paid</Typography.Text>
      )
  },
  {
    title: 'Created',
    key: 'created',
    render: (_, row) => formatDateTime(row.createdAt)
  }
]
/* eslint-enable react/display-name */

// The worklist. Everything our team needs to find one order by whatever the customer told them on
// the phone — a VIN, a number they are reading off a WhatsApp message, a payment reference.
//
// Attention-first is a deliberate second ordering rather than the default: most days the list is
// read newest-first, and only when something is wrong does the worst-first order earn its place.
const OrdersList = () => {
  const [orders, setOrders] = useState([])
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(false)
  const [count, setCount] = useState(0)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState({ q: '', status: [], channel: [], attention: false })
  const [search, setSearch] = useState('')

  const load = useCallback(async () => {
    const query = buildOrdersQuery({ ...filters, page })
    const response = await getRequest(
      `${vehicleHistoryOrdersUrl}${query}`,
      'orders',
      setLoading
    )
    if (!response.success) return
    const data = response.data || {}
    // The house paginator answers `{results, count}`; a plain list is still accepted so a server
    // that stops paginating does not blank the page.
    setOrders(Array.isArray(data) ? data : data.results || [])
    setCount(Array.isArray(data) ? data.length : data.count || 0)
  }, [filters, page])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    getRequest(vehicleHistoryProductsUrl, 'report packages', () => {}).then(response => {
      if (response.success) setProducts((response.data || {}).results || [])
    })
  }, [])

  const update = patch => {
    setPage(1)
    setFilters(current => ({ ...current, ...patch }))
  }

  return (
    <Card
      title="Vehicle history orders"
      extra={
        <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
          Refresh
        </Button>
      }
    >
      <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="VIN, chassis, phone or payment reference"
            value={search}
            onChange={event => setSearch(event.target.value)}
            onPressEnter={() => update({ q: search })}
            onBlur={() => update({ q: search })}
          />
        </Col>
        <Col xs={24} md={6}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            placeholder="Status"
            value={filters.status}
            onChange={status => update({ status })}
          >
            {Object.keys(STATUS_LABELS).map(status => (
              <Select.Option key={status} value={status}>
                {STATUS_LABELS[status]}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} md={5}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: '100%' }}
            placeholder="Came from"
            value={filters.channel}
            onChange={channel => update({ channel })}
          >
            {Object.keys(CHANNEL_LABELS).map(channel => (
              <Select.Option key={channel} value={channel}>
                {CHANNEL_LABELS[channel]}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} md={5}>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder="Package"
            value={filters.product}
            onChange={product => update({ product })}
          >
            {products.map(product => (
              <Select.Option key={product.key} value={product.key}>
                {product.name}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={12} md={5}>
          <Select
            allowClear
            style={{ width: '100%' }}
            placeholder="Market"
            value={filters.market}
            onChange={market => update({ market })}
          >
            {MARKET_ORDER.map(market => (
              <Select.Option key={market} value={market}>
                {MARKET_LABELS[market]}
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col xs={24} md={10}>
          <RangePicker
            style={{ width: '100%' }}
            onChange={range =>
              update({
                from: range && range[0] ? range[0].format('YYYY-MM-DD') : undefined,
                to: range && range[1] ? range[1].format('YYYY-MM-DD') : undefined
              })
            }
          />
        </Col>
        <Col xs={12} md={7}>
          <Space>
            <Switch
              checked={filters.paid === true}
              onChange={checked => update({ paid: checked ? true : undefined })}
            />
            <span>Paid only</span>
          </Space>
        </Col>
        <Col xs={12} md={7}>
          <Space>
            <Switch
              checked={!!filters.attention}
              onChange={attention => update({ attention })}
            />
            <span>Needs attention</span>
          </Space>
        </Col>
      </Row>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        dataSource={orders}
        columns={orderColumns()}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page,
          total: count,
          pageSize: 20,
          showSizeChanger: false,
          onChange: setPage
        }}
      />
    </Card>
  )
}

export default OrdersList
